import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"

/**
 * OKS-457 — Living-crew social-proof teaser (§01, UX Spec §2.3).
 *
 * Shows an aggregate, brand-isolated signal that the crew is alive: a
 * creator count + invite-views-this-month, fronted by a small cluster of
 * **decorative** avatar tiles.
 *
 * # Brand isolation (P0 — public pre-auth surface, TRD KD-4)
 *
 * The avatar tiles are PURELY DECORATIVE — their initials come from a
 * static letter pool (`DECOR_INITIALS`), NOT from any real crew member.
 * The component receives only two integers (`crewCount`,
 * `viewsThisMonth`); no member name, handle, id, or face ever reaches it
 * or the DOM. The backend ships zero per-member identity to back this.
 *
 * # Empty / small-crew handling (OKS-665)
 *
 * Renders nothing unless the crew is meaningfully populated —
 * `crewCount > MIN_CREW_COUNT_FOR_TEASER` (i.e. 11+). At or below the
 * threshold (including null/0) a low number is anti-social-proof and
 * would undermine the first impression on the public invite-landing, so
 * the teaser is omitted with no replacement. The views sub-line is shown
 * only when `viewsThisMonth > 0`.
 */
export interface LivingCrewTeaserProps {
  brandName: string
  crewCount: number | null | undefined
  viewsThisMonth: number | null | undefined
  testId?: string
}

/**
 * Decorative initial letters for the avatar cluster. Mirrors the locked
 * Edge frame ("A C E G H") — these are NOT real member initials; they
 * exist only to give the cluster visual texture. Capped at the live crew
 * count so the cluster never implies more members than exist.
 */
const DECOR_INITIALS = ["A", "C", "E", "G"] as const

/**
 * OKS-665 — Minimum crew size before the social-proof teaser renders.
 *
 * Below/at this count the teaser is hidden: a low number ("2 creators")
 * reads as *weak* social proof on the public invite-landing and
 * undermines the invitee's first impression. The gate is strictly
 * greater-than, so exactly {@link MIN_CREW_COUNT_FOR_TEASER} is hidden
 * and the first shown value is one more. This is the ONE tunable place —
 * change this value to move the threshold everywhere. Exported so the
 * boundary unit test asserts against it rather than a hardcoded literal.
 */
export const MIN_CREW_COUNT_FOR_TEASER = 10

export function LivingCrewTeaser({
  brandName,
  crewCount,
  viewsThisMonth,
  testId = "living-crew-teaser",
}: LivingCrewTeaserProps) {
  const { t } = useTranslation()

  if (!crewCount || crewCount <= MIN_CREW_COUNT_FOR_TEASER) return null

  const tileCount = Math.min(crewCount, DECOR_INITIALS.length)
  const tiles = DECOR_INITIALS.slice(0, tileCount)
  const hasViews = typeof viewsThisMonth === "number" && viewsThisMonth > 0

  return (
    <div
      data-testid={testId}
      className="bg-primary/10 flex items-center gap-3 rounded-2xl px-4 py-3.5"
    >
      <div aria-hidden="true" className="flex shrink-0 items-center">
        {tiles.map((letter, index) => (
          <Avatar
            key={letter}
            className={cn(
              "ring-background size-8 ring-2",
              index > 0 && "-ml-3",
            )}
          >
            <AvatarFallback className="bg-primary/15 text-primary text-xs font-semibold">
              {letter}
            </AvatarFallback>
          </Avatar>
        ))}
      </div>
      <div className="flex min-w-0 flex-col">
        <span
          data-testid={`${testId}-count`}
          className="text-foreground text-sm font-semibold"
        >
          {t("invites.landing.crew_proof.count", {
            n: crewCount,
            brand: brandName,
          })}
        </span>
        <span className="text-muted-foreground text-[13px]">
          {hasViews
            ? t("invites.landing.crew_proof.views", { n: viewsThisMonth })
            : t("invites.landing.crew_proof.join")}
        </span>
      </div>
    </div>
  )
}

export default LivingCrewTeaser
